// Canadian French (fr-CA) translations for the Android-parity worker mobile
// web/PWA shell: navigation, punch and attendance actions, offline/sync status,
// browser capability explanations, the permissions center, documents, time,
// messaging, and the mobile Learning Portal / AI Coach.
//
// Keys are the canonical English strings, matching the convention used by the
// other dictionaries. This module is merged into the `fr` dictionary in
// I18nContext; anything missing falls back to the English key at runtime.

const frWorkerMobileLocalization: Record<string, string> = {
  // Navigation and shell
  'Today': 'Aujourd’hui',
  'Shifts': 'Quarts',
  'Time': 'Temps',
  'More': 'Plus',
  'Worker navigation': 'Navigation du travailleur',
  'Open learning portal': 'Ouvrir le portail d’apprentissage',
  'Refresh': 'Actualiser',
  'Offline': 'Hors ligne',
  'Back': 'Retour',
  'Close': 'Fermer',
  'Open': 'Ouvrir',
  'Remove': 'Retirer',
  'Sign out': 'Se déconnecter',
  '{count} unread messages': '{count} messages non lus',

  // Home / punch
  'Loading your shifts…': 'Chargement de vos quarts…',
  'Assignment': 'Affectation',
  'Assigned shift': 'Quart assigné',
  'Time pending': 'Heure à confirmer',
  'Worksite': 'Lieu de travail',
  'Directions': 'Itinéraire',
  'Punch in': 'Pointer l’arrivée',
  'Punch out': 'Pointer le départ',
  'Start break': 'Commencer la pause',
  'End break': 'Terminer la pause',
  'Return to work': 'Reprendre le travail',
  'Resume shift': 'Reprendre le quart',
  'On the way': 'En route',
  'On the way sent': 'Départ signalé',
  'Report a problem': 'Signaler un problème',
  'Punched in.': 'Arrivée pointée.',
  'Punched out.': 'Départ pointé.',
  'Break started.': 'Pause commencée.',
  'Break ended.': 'Pause terminée.',
  'Your organization was told you are on the way.': 'Votre organisation a été informée que vous êtes en route.',
  'No shift today': 'Aucun quart aujourd’hui',
  'When you are assigned a shift it appears here with your punch controls.': 'Dès qu’un quart vous est assigné, il apparaît ici avec vos commandes de pointage.',
  'Next shift': 'Prochain quart',
  'All shifts': 'Tous les quarts',
  'Location pending': 'Lieu à confirmer',
  'Your day': 'Votre journée',
  'Find work': 'Trouver du travail',
  'Messages': 'Messages',
  'Learning': 'Apprentissage',
  'Profile': 'Profil',
  'Cancellation request: {status}': 'Demande d’annulation : {status}',
  'Location captured to {accuracy} m accuracy.': 'Position captée à {accuracy} m de précision.',

  // Attendance exceptions
  'Running late': 'En retard',
  'Cannot attend': 'Absence impossible à éviter',
  'Request cancellation': 'Demander une annulation',
  'What is happening?': 'Que se passe-t-il ?',
  'Add a note (optional)': 'Ajouter une note (facultatif)',
  'Your organization sees this note.': 'Votre organisation voit cette note.',
  'Send report': 'Envoyer le signalement',
  'Your report was sent to your organization.': 'Votre signalement a été envoyé à votre organisation.',
  'No attendance actions are available for this shift right now.': 'Aucune action de présence n’est disponible pour ce quart en ce moment.',

  // Offline / sync
  'Waiting to sync': 'En attente de synchronisation',
  'These actions are saved on this device and stay visible until the server confirms them.': 'Ces actions sont enregistrées sur cet appareil et restent visibles jusqu’à confirmation du serveur.',
  '{count} pending sync': '{count} en attente de synchronisation',
  '{count} syncing': '{count} en cours de synchronisation',
  '{count} failed': '{count} en échec',
  '{count} awaiting review': '{count} en attente de révision',
  'Sync now': 'Synchroniser maintenant',
  'Waiting for a connection': 'En attente d’une connexion',
  'You are offline': 'Vous êtes hors ligne',
  'Punch and attendance actions are queued on this device and submitted when you reconnect. Nothing is recorded on the server until then.': 'Les pointages et signalements sont mis en file sur cet appareil et envoyés à la reconnexion. Rien n’est enregistré sur le serveur avant cela.',
  'Saved on this device. It will be submitted for review when you reconnect.': 'Enregistré sur cet appareil. L’envoi pour révision se fera à la reconnexion.',
  'Saved on this device. It will be sent when you reconnect.': 'Enregistré sur cet appareil. L’envoi se fera à la reconnexion.',
  'This action needs your location and cannot be saved offline without it.': 'Cette action exige votre position et ne peut pas être enregistrée hors ligne sans elle.',
  'This browser cannot store offline actions, so this punch cannot be saved until you reconnect.': 'Ce navigateur ne peut pas stocker d’actions hors ligne ; ce pointage ne peut donc pas être enregistré avant la reconnexion.',
  'This browser cannot store offline actions, so this cannot be saved until you reconnect.': 'Ce navigateur ne peut pas stocker d’actions hors ligne ; cela ne peut donc pas être enregistré avant la reconnexion.',

  // Location capability
  'Location is not available in this browser': 'La localisation n’est pas disponible dans ce navigateur',
  'Location permission is blocked': 'L’autorisation de localisation est bloquée',
  'Onsite punches need a browser that can share your location. Open the app in Chrome or Safari on your phone.': 'Les pointages sur place exigent un navigateur capable de partager votre position. Ouvrez l’application dans Chrome ou Safari sur votre téléphone.',
  'Your browser is blocking location for this site. Re-enable it in browser settings, then return here.': 'Votre navigateur bloque la localisation pour ce site. Réactivez-la dans les réglages du navigateur, puis revenez ici.',
  'Open permissions': 'Ouvrir les autorisations',
  'Location permission is required for an onsite punch. Enable it for this site and try again.': 'L’autorisation de localisation est requise pour un pointage sur place. Activez-la pour ce site et réessayez.',
  'This browser cannot share your location, so an onsite punch is not possible here.': 'Ce navigateur ne peut pas partager votre position ; un pointage sur place est donc impossible ici.',
  'Your browser returned an out-of-date location. Move outdoors or try again in a moment.': 'Votre navigateur a renvoyé une position périmée. Sortez à l’extérieur ou réessayez dans un instant.',
  'The browser did not return a location in time. Try again.': 'Le navigateur n’a pas renvoyé de position à temps. Réessayez.',
  'Your location could not be determined. Try again once you have a clearer view of the sky.': 'Votre position n’a pas pu être déterminée. Réessayez avec une vue plus dégagée du ciel.',

  // Compliance / documents
  'Documents need attention': 'Des documents requièrent votre attention',
  'These were flagged by your organization for this assignment.': 'Votre organisation les a signalés pour cette affectation.',
  'Open documents': 'Ouvrir les documents',
  'Certifications & documents': 'Certifications et documents',
  'Only an authorized reviewer can verify a document. Anything read from a photo is a suggestion for you to confirm.': 'Seul un réviseur autorisé peut vérifier un document. Toute donnée lue sur une photo est une suggestion que vous devez confirmer.',
  'Add': 'Ajouter',
  'Add a document': 'Ajouter un document',
  'Document type': 'Type de document',
  'Profile photo': 'Photo de profil',
  'Certificate': 'Certificat',
  'Licence': 'Permis',
  'Medical clearance': 'Autorisation médicale',
  'Other': 'Autre',
  'Title': 'Titre',
  'Take a photo': 'Prendre une photo',
  'Choose a file': 'Choisir un fichier',
  'Upload': 'Téléverser',
  'Document uploaded for review.': 'Document téléversé pour révision.',
  'Profile photo updated.': 'Photo de profil mise à jour.',
  'Document removed.': 'Document retiré.',
  'Remove this document?': 'Retirer ce document ?',
  'Confirm details': 'Confirmer les renseignements',
  'Details confirmed.': 'Renseignements confirmés.',
  'No documents yet': 'Aucun document pour l’instant',
  'Add a certificate, licence, or ID so you qualify for more work.': 'Ajoutez un certificat, un permis ou une pièce d’identité pour être admissible à plus de travail.',
  'Expires {date}': 'Expire le {date}',
  'Camera capture is unavailable': 'La capture par caméra est indisponible',
  'Your organization has not enabled camera capture. You can still upload a photo from your files.': 'Votre organisation n’a pas activé la capture par caméra. Vous pouvez tout de même téléverser une photo depuis vos fichiers.',
  'This browser cannot open the camera. You can still upload a photo from your files.': 'Ce navigateur ne peut pas ouvrir la caméra. Vous pouvez tout de même téléverser une photo depuis vos fichiers.',

  // Shifts
  'Upcoming': 'À venir',
  'Applied': 'Candidatures',
  'History': 'Historique',
  'No upcoming shifts': 'Aucun quart à venir',
  'Booked shifts appear here as soon as they are confirmed.': 'Les quarts réservés apparaissent ici dès leur confirmation.',
  'Recommended for you': 'Recommandé pour vous',
  'Matched by your organization from your skills and work area.': 'Apparié par votre organisation selon vos compétences et votre secteur de travail.',
  'Open shift': 'Quart ouvert',
  'Eligible': 'Admissible',
  'Not eligible': 'Non admissible',
  '{count} needed': '{count} requis',
  'Apply': 'Postuler',
  'Application submitted.': 'Candidature soumise.',
  'No open shifts right now': 'Aucun quart ouvert pour l’instant',
  'New marketplace shifts appear here when your organization posts them.': 'Les nouveaux quarts du marché apparaissent ici lorsque votre organisation les publie.',
  'Shift application': 'Candidature à un quart',
  'Withdraw': 'Retirer',
  'Application withdrawn.': 'Candidature retirée.',
  'No applications yet': 'Aucune candidature pour l’instant',
  'Shifts you apply for appear here with their current status.': 'Les quarts auxquels vous postulez apparaissent ici avec leur statut actuel.',
  'Completed shift': 'Quart terminé',
  'No shift history yet': 'Aucun historique de quarts',
  'Completed shifts appear here.': 'Les quarts terminés apparaissent ici.',

  // Messaging
  'Conversation': 'Conversation',
  'Loading conversation…': 'Chargement de la conversation…',
  'No messages yet': 'Aucun message pour l’instant',
  'Start the conversation below.': 'Commencez la conversation ci-dessous.',
  'Your message': 'Votre message',
  'Write a message': 'Écrire un message',
  'Send': 'Envoyer',
  'Replies are not enabled for this conversation.': 'Les réponses ne sont pas activées pour cette conversation.',
  'No conversations yet': 'Aucune conversation pour l’instant',
  'Messages from your supervisors and agency appear here.': 'Les messages de vos superviseurs et de votre agence apparaissent ici.',
  'This conversation could not be opened.': 'Cette conversation n’a pas pu être ouverte.',
  'Your message could not be sent.': 'Votre message n’a pas pu être envoyé.',
  'Team': 'Équipe',

  // Time & pay
  'Time & pay': 'Temps et paie',
  'Submitted, adjusted, and final payable hours are calculated by your organization.': 'Les heures soumises, ajustées et finales payables sont calculées par votre organisation.',
  'Submitted': 'Soumis',
  'Breaks': 'Pauses',
  'Final payable': 'Payable final',
  'Pay': 'Paie',
  'Payroll': 'Paie',
  'hours': 'heures',
  'min': 'min',
  'Rate not shown': 'Taux non affiché',
  'Settled': 'Réglé',
  'Exported to payroll': 'Exporté vers la paie',
  'Locked for payroll': 'Verrouillé pour la paie',
  'Ready for payroll': 'Prêt pour la paie',
  'Not ready': 'Pas prêt',
  'Not available': 'Non disponible',
  'Adjustment': 'Ajustement',
  'Rejection': 'Refus',
  'Your dispute': 'Votre contestation',
  'Resolution': 'Résolution',
  'Accept adjustment': 'Accepter l’ajustement',
  'Adjustment accepted.': 'Ajustement accepté.',
  'Dispute': 'Contester',
  'Dispute this timesheet': 'Contester cette feuille de temps',
  'Describe the issue with this timesheet': 'Décrivez le problème lié à cette feuille de temps',
  'Submit dispute': 'Soumettre la contestation',
  'Dispute submitted.': 'Contestation soumise.',
  'Date not available': 'Date non disponible',
  'No timesheets yet': 'Aucune feuille de temps',
  'Completed shifts appear here for you to review.': 'Les quarts terminés apparaissent ici pour révision.',

  // Profile
  'Profile unavailable': 'Profil indisponible',
  'Link an organization, or pull down to refresh your worker profile.': 'Liez une organisation ou tirez vers le bas pour actualiser votre profil de travailleur.',
  'Worker profile': 'Profil de travailleur',
  'Profile completion': 'Progression du profil',
  '{percent}% complete': '{percent} % complété',
  'Edit details': 'Modifier les renseignements',
  'First name': 'Prénom',
  'Last name': 'Nom',
  'Phone': 'Téléphone',
  'Languages': 'Langues',
  'Mailing city': 'Ville postale',
  'Mailing region': 'Région postale',
  'Matching radius (km)': 'Rayon d’appariement (km)',
  'Emergency contact': 'Personne à contacter en cas d’urgence',
  'Emergency phone': 'Téléphone d’urgence',
  'Save profile': 'Enregistrer le profil',
  'Profile saved.': 'Profil enregistré.',
  'Skills & progression': 'Compétences et progression',
  'Levels, evidence, and next steps are calculated by your organization.': 'Les niveaux, les preuves et les prochaines étapes sont calculés par votre organisation.',
  'Skill': 'Compétence',
  'Certified': 'Certifié',
  '{hours} verified hours · {shifts} shifts': '{hours} heures vérifiées · {shifts} quarts',
  'Next: {level}': 'Prochain : {level}',
  'No skill progression yet': 'Aucune progression de compétences',
  'Completed shifts and verified evidence build your levels here.': 'Les quarts terminés et les preuves vérifiées construisent vos niveaux ici.',
  'Notifications': 'Notifications',
  'Mark all read': 'Tout marquer comme lu',
  'All notifications marked read.': 'Toutes les notifications ont été marquées comme lues.',
  'Update': 'Mise à jour',
  'Open related item': 'Ouvrir l’élément lié',
  'No notifications': 'Aucune notification',
  'Shift and timesheet updates appear here.': 'Les mises à jour de quarts et de feuilles de temps apparaissent ici.',
  'Account & settings': 'Compte et réglages',
  'App permissions': 'Autorisations de l’application',

  // Availability
  'Availability': 'Disponibilité',
  'Set your usual week and time off. Assigned shifts still take priority.': 'Définissez votre semaine habituelle et vos congés. Les quarts assignés demeurent prioritaires.',
  'Usual Week': 'Semaine habituelle',
  'Time Off': 'Congés',
  'Day': 'Jour',
  'Start': 'Début',
  'End': 'Fin',
  'Save window': 'Enregistrer la plage',
  'Availability saved.': 'Disponibilité enregistrée.',
  'Availability removed.': 'Disponibilité retirée.',
  'One-time window': 'Plage ponctuelle',
  'No usual week set': 'Aucune semaine habituelle définie',
  'Add the hours you can normally work.': 'Ajoutez les heures où vous pouvez normalement travailler.',
  'Starts': 'Début',
  'Ends': 'Fin',
  'Reason': 'Motif',
  'Add time off': 'Ajouter un congé',
  'Time off saved.': 'Congé enregistré.',
  'Time off removed.': 'Congé retiré.',
  'Unavailable': 'Non disponible',
  'No time off booked': 'Aucun congé prévu',
  'Add dates you cannot work.': 'Ajoutez les dates où vous ne pouvez pas travailler.',

  // More / decisions
  'Switch workspace': 'Changer d’espace de travail',
  'You have access to more than one area.': 'Vous avez accès à plus d’un espace.',
  'Manage your availability, profile, and app settings.': 'Gérez votre disponibilité, votre profil et les réglages de l’application.',
  'Usual week and time off': 'Semaine habituelle et congés',
  'My Work': 'Mon travail',
  'Project time and timesheets': 'Temps de projet et feuilles de temps',
  'Details, documents, and skills': 'Renseignements, documents et compétences',
  'Decisions & Appeals': 'Décisions et appels',
  'Why an action was not available, and how to ask for review': 'Pourquoi une action était indisponible et comment demander une révision',
  'Mentorship': 'Mentorat',
  'Your mentors and sessions': 'Vos mentors et vos séances',
  'Lessons, progress, and your AI coach': 'Leçons, progression et votre coach IA',
  'Location, notifications, camera, and offline': 'Localisation, notifications, caméra et hors ligne',
  'These explain why something was not available and whether you can ask for a human review.': 'Ceci explique pourquoi un élément était indisponible et si vous pouvez demander une révision humaine.',
  'Request human review': 'Demander une révision humaine',
  'Tell the reviewer what you would like them to look at': 'Indiquez au réviseur ce que vous souhaitez lui faire examiner',
  'Review requested.': 'Révision demandée.',
  'Review requested — status: {status}': 'Révision demandée — statut : {status}',
  'No recorded decisions': 'Aucune décision enregistrée',
  'Decisions about your access to work appear here with their reasons.': 'Les décisions concernant votre accès au travail apparaissent ici avec leurs motifs.',

  // Permissions center
  'These control what this browser is allowed to do. Your organization still decides what the app itself can do.': 'Ceci contrôle ce que ce navigateur est autorisé à faire. Votre organisation décide toujours de ce que l’application peut faire.',
  'Install this app': 'Installer cette application',
  'Installing gives you a full-screen app, a Home Screen icon, and offline access to your shifts.': 'L’installation vous donne une application plein écran, une icône sur l’écran d’accueil et un accès hors ligne à vos quarts.',
  'Installed': 'Installée',
  'Not installed': 'Non installée',
  'Install app': 'Installer l’application',
  'Open the browser share menu.': 'Ouvrez le menu de partage du navigateur.',
  'Choose “Add to Home Screen”.': 'Choisissez « Sur l’écran d’accueil ».',
  'Open NexusWorkforce360 from your Home Screen.': 'Ouvrez NexusWorkforce360 depuis votre écran d’accueil.',
  'Granted': 'Accordée',
  'Available': 'Disponible',
  'Not requested': 'Non demandée',
  'Denied': 'Refusée',
  'Unsupported in this browser': 'Non prise en charge par ce navigateur',
  'Requires browser settings': 'Nécessite les réglages du navigateur',
  'Requires Home Screen install': 'Nécessite l’installation sur l’écran d’accueil',
  'Turned off by your organization': 'Désactivée par votre organisation',
  'Setup required': 'Configuration requise',
  'Location': 'Localisation',
  'Used when you punch in or out, mark yourself on the way, and while you are on an active shift.': 'Utilisée lors du pointage d’arrivée ou de départ, du signalement « en route » et pendant un quart actif.',
  'Your organization has not enabled browser location for this app.': 'Votre organisation n’a pas activé la localisation par navigateur pour cette application.',
  'This browser does not provide location. Open the app in Chrome or Safari on your phone.': 'Ce navigateur ne fournit pas de localisation. Ouvrez l’application dans Chrome ou Safari sur votre téléphone.',
  'You previously refused location. Re-enable it for this site in your browser settings, then reload.': 'Vous avez déjà refusé la localisation. Réactivez-la pour ce site dans les réglages du navigateur, puis rechargez.',
  'Allow location when your browser asks. Onsite punches cannot be submitted without it.': 'Autorisez la localisation lorsque le navigateur le demande. Les pointages sur place sont impossibles sans elle.',
  'Allow location': 'Autoriser la localisation',
  'Shift reminders, assignment changes, messages, and timesheet updates.': 'Rappels de quarts, changements d’affectation, messages et mises à jour de feuilles de temps.',
  'Your organization has not enabled browser notifications for this app.': 'Votre organisation n’a pas activé les notifications du navigateur pour cette application.',
  'Browser notifications are not configured for this deployment yet.': 'Les notifications du navigateur ne sont pas encore configurées pour ce déploiement.',
  'On iPhone and iPad, notifications work only after you add this app to your Home Screen.': 'Sur iPhone et iPad, les notifications fonctionnent uniquement après l’ajout de cette application à l’écran d’accueil.',
  'You previously refused notifications. Re-enable them for this site in your browser settings.': 'Vous avez déjà refusé les notifications. Réactivez-les pour ce site dans les réglages du navigateur.',
  'Allow notifications when your browser asks.': 'Autorisez les notifications lorsque le navigateur le demande.',
  'Allow notifications': 'Autoriser les notifications',
  'To receive shift alerts on this iPhone': 'Pour recevoir les alertes de quarts sur cet iPhone',
  '1. Add NexusWorkforce360 to your Home Screen. 2. Open it from the Home Screen. 3. Enable notifications when prompted.': '1. Ajoutez NexusWorkforce360 à votre écran d’accueil. 2. Ouvrez-la depuis l’écran d’accueil. 3. Activez les notifications lorsque cela vous est demandé.',
  'Push notifications on this device': 'Notifications poussées sur cet appareil',
  'Delivers the same notifications you already receive in the app.': 'Livre les mêmes notifications que celles déjà reçues dans l’application.',
  'On': 'Activées',
  'Off': 'Désactivées',
  'Turn on notifications': 'Activer les notifications',
  'Turn off on this device': 'Désactiver sur cet appareil',
  'Notifications are blocked for this site. Re-enable them in your browser settings and try again.': 'Les notifications sont bloquées pour ce site. Réactivez-les dans les réglages du navigateur et réessayez.',
  'Camera': 'Caméra',
  'Take a profile photo or capture a certificate, licence, or ID.': 'Prenez une photo de profil ou capturez un certificat, un permis ou une pièce d’identité.',
  'Your organization has not enabled browser document capture.': 'Votre organisation n’a pas activé la capture de documents par navigateur.',
  'Allow camera access when your browser asks. You can always upload an existing photo instead.': 'Autorisez l’accès à la caméra lorsque le navigateur le demande. Vous pouvez toujours téléverser une photo existante à la place.',
  'Allow camera': 'Autoriser la caméra',
  'Microphone': 'Microphone',
  'Used only where a voice feature is enabled for your organization.': 'Utilisé uniquement lorsqu’une fonction vocale est activée pour votre organisation.',
  'Allow microphone access when your browser asks.': 'Autorisez l’accès au microphone lorsque le navigateur le demande.',
  'Allow microphone': 'Autoriser le microphone',
  'Offline storage': 'Stockage hors ligne',
  'Keeps your shifts readable offline and queues punches until you reconnect.': 'Garde vos quarts lisibles hors ligne et met les pointages en file jusqu’à la reconnexion.',
  'Offline data is a convenience copy, never the official record. Anything waiting to sync stays visible until the server confirms it.': 'Les données hors ligne sont une copie pratique, jamais le registre officiel. Tout ce qui attend la synchronisation reste visible jusqu’à confirmation du serveur.',
  'Background sync': 'Synchronisation en arrière-plan',
  'Lets the app finish sending queued actions after you close it.': 'Permet à l’application de terminer l’envoi des actions en file après sa fermeture.',
  'This browser has no background sync. Queued actions are sent the next time you open the app while online — nothing is lost.': 'Ce navigateur n’offre pas de synchronisation en arrière-plan. Les actions en file sont envoyées à la prochaine ouverture de l’application en ligne — rien n’est perdu.',
  'Background location is not available in a browser': 'La localisation en arrière-plan n’est pas disponible dans un navigateur',
  'While this app is open, location works normally. When your phone closes or suspends it, the browser stops sharing location — so continuous tracking and automatic geofence punches are not available here. Punching in and out still works whenever you open the app.': 'Tant que cette application est ouverte, la localisation fonctionne normalement. Lorsque votre téléphone la ferme ou la suspend, le navigateur cesse de partager la position — le suivi continu et les pointages automatiques par géorepérage ne sont donc pas offerts ici. Le pointage d’arrivée et de départ fonctionne toujours à l’ouverture de l’application.',

  // Org link
  'Link an organization to unlock work operations.': 'Liez une organisation pour débloquer les opérations de travail.',
  'You can keep using Learning while your worker profile has no organization.': 'Vous pouvez continuer à utiliser l’Apprentissage tant que votre profil de travailleur n’a pas d’organisation.',
  'Invite code': 'Code d’invitation',
  'Link organization': 'Lier l’organisation',
  'Organization linked.': 'Organisation liée.',

  // Learning & coach
  'Learn': 'Apprendre',
  'Coach': 'Coach',
  'Loading your learning…': 'Chargement de votre apprentissage…',
  'Loading your coach…': 'Chargement de votre coach…',
  'Learning could not be loaded right now.': 'L’apprentissage n’a pas pu être chargé pour le moment.',
  'Learning is not set up yet': 'L’apprentissage n’est pas encore configuré',
  'Start your first lesson to build your learning profile.': 'Commencez votre première leçon pour bâtir votre profil d’apprentissage.',
  'Your learning': 'Votre apprentissage',
  '{days} day streak': 'Série de {days} jours',
  '{xp} XP': '{xp} XP',
  'Finish your learning setup to get personalized recommendations.': 'Terminez la configuration de votre apprentissage pour obtenir des recommandations personnalisées.',
  'Assigned to you': 'Qui vous est assigné',
  'Assigned learning': 'Apprentissage assigné',
  'Due {date}': 'Échéance : {date}',
  'Continue learning': 'Poursuivre l’apprentissage',
  'Level {level}': 'Niveau {level}',
  'Recommended next': 'Recommandé ensuite',
  'Chosen by the platform from your progress and work.': 'Choisi par la plateforme selon votre progression et votre travail.',
  'Recommended lesson': 'Leçon recommandée',
  'Upcoming coaching': 'Séances de coaching à venir',
  'Coaching session': 'Séance de coaching',
  'Today’s brief': 'Le point du jour',
  '{minutes} min · {difficulty}': '{minutes} min · {difficulty}',
  'Your learning plan': 'Votre plan d’apprentissage',
  'Week {week}': 'Semaine {week}',
  'Ask your coach': 'Demandez à votre coach',
  'Your question': 'Votre question',
  'Ask': 'Demander',
  'The coach could not answer right now.': 'Le coach n’a pas pu répondre pour le moment.',
  'Because of your work': 'En raison de votre travail',
  'Weekly review': 'Bilan hebdomadaire',

  // Queued-action failures
  'These could not be submitted': 'Ces éléments n’ont pas pu être envoyés',
  'Recorded too long ago to submit automatically. Ask your supervisor to record it.': 'Enregistré il y a trop longtemps pour un envoi automatique. Demandez à votre superviseur de l’enregistrer.',
  'Recorded without a location, so it cannot be submitted automatically.': 'Enregistré sans position ; l’envoi automatique est donc impossible.',
  'This could not be submitted. Ask your supervisor for help.': 'Cet élément n’a pas pu être envoyé. Demandez de l’aide à votre superviseur.',

  // Shared states
  'We couldn’t load this section.': 'Nous n’avons pas pu charger cette section.',
  'Try again': 'Réessayer',

  // Job and assignment maps
  'Job Bank view': 'Vue de la banque d’emplois',
  'List': 'Liste',
  'Map': 'Carte',
  'Job map': 'Carte des emplois',
  'Worksite map': 'Carte du lieu de travail',
  'Use my location': 'Utiliser ma position',
  'Finding you…': 'Localisation en cours…',
  'My location': 'Ma position',
  'No map-visible jobs': 'Aucun emploi visible sur la carte',
  'Jobs without a shareable map location remain available in List view.': 'Les emplois sans position partageable restent disponibles dans la vue Liste.',
  'Location permission is off. You can still browse jobs without sharing your location.': 'L’autorisation de localisation est désactivée. Vous pouvez quand même parcourir les emplois sans partager votre position.',
  'My location is unavailable. You can still browse jobs.': 'Ma position est indisponible. Vous pouvez quand même parcourir les emplois.',
  'Map tiles are unavailable. Job details are still shown below.': 'Les tuiles de carte sont indisponibles. Les détails de l’emploi restent affichés ci-dessous.',
  'Select a marker to preview a job.': 'Sélectionnez un repère pour prévisualiser un emploi.',
  'Jobs at this location': 'Emplois à cet endroit',
  'View Job': 'Voir l’emploi',
  'Approximate location. Exact worksite shown after assignment.': 'Emplacement approximatif. Le lieu de travail exact est affiché après l’affectation.',
  'Exact worksite': 'Lieu de travail exact',
  'Approximate location': 'Emplacement approximatif',
  'Open Directions': 'Ouvrir l’itinéraire',
  'Location permission is off. You can still view the worksite and open directions.': 'L’autorisation de localisation est désactivée. Vous pouvez quand même voir le lieu de travail et ouvrir l’itinéraire.',
  'My location is unavailable. You can still view the worksite.': 'Ma position est indisponible. Vous pouvez quand même voir le lieu de travail.',
}

export default frWorkerMobileLocalization
